import { TeambinderApiService } from '@src/api';
import {
  KAKAO_REDIRECT_URL_COMMAND,
  PUBLIC_URL,
} from '@src/constants/constant';
import useLogout from '@src/hooks/auth/useLogout';
import { themeCurrentThemeAtom } from '@src/store/theme';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Input,
  Modal,
  Progress,
  Radio,
  Switch,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SettingQuotaPane from './SettingQuotaPane';
import 'cleave.js/dist/addons/cleave-phone.kr';
import Cleave from 'cleave.js/react';

const { confirm, info } = Modal;

const TabInfo = () => {
  const {
    data: userResponse,
    refetch: refetchSetting,
    isLoading: isLoadingUser,
  } = useQuery(['member'], () => TeambinderApiService.getPersonalSettings({}), {
    suspense: false,
  });
  const [normalizedPhoneNumber, setNormalizedPhoneNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  useEffect(() => {
    if (userResponse?.data?.notification?.email?.email) {
      setEmail(userResponse?.data?.notification?.email?.email);
    }
    if (userResponse?.data?.notification?.kakao?.mobile) {
      const phone = userResponse?.data?.notification?.kakao?.mobile;
      const normalized = '0' + phone.split(/[- ]/).slice(1).join(''); // "+82 10-3333-5555" -> "01033335555"
      setPhoneNumber(normalized);
      setNormalizedPhoneNumber(normalized);
    }
  }, [userResponse?.data]);
  const [currentTheme, setCurrentTheme] = useAtom(themeCurrentThemeAtom);
  const handleClickSetTheme = () => {
    if (currentTheme === 'dark') {
      setCurrentTheme('light');
    } else {
      setCurrentTheme('dark');
    }
  };
  const _logout = useLogout();
  const logout = async () => {
    _logout();
    // window.location.href = `https://kauth.kakao.com/oauth/logout?client_id=7fa35d903c130394963af980d96edd38&logout_redirect_uri=${PUBLIC_URL}&prompt=login`;
  };

  const navigate = useNavigate();
  const { data: currentSubscription, isLoading: isLoadingSubscription } =
    useQuery(
      ['current-subscription'],
      () => TeambinderApiService.getLatestSubscription({ q: 'latest' }),
      { refetchInterval: 3000, suspense: false },
    );

  // TODO: openapi 스펙 이상함
  const currentPlan =
    currentSubscription?.data?.plan?.subscriptionPackage?.subscriptionPlan ??
    '';

  const isLoading = isLoadingUser || isLoadingSubscription;

  if (isLoading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const cardStyle = {
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    maxWidth: '800px',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        paddingLeft: 0,
        paddingTop: '20px',
        fontSize: '16px',
      }}
    >
      {/* 카카오 알림 설정 카드 */}
      <Card style={cardStyle}>
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ display: 'inline-block', margin: 0 }}>
            카카오로 알림 받기
            {userResponse?.data?.notification?.kakao?.mobile && (
              <Switch
                checked={userResponse?.data?.notification?.kakao?.enabled}
                style={{ marginLeft: '10px' }}
                onClick={async (checked) => {
                  await TeambinderApiService.modifyPersonalSettings({
                    settingName: 'notification.kakao.enabled',
                    value: String(checked),
                  });
                  refetchSetting();
                }}
              />
            )}
          </h4>
          <div style={{ marginTop: '8px' }}>
            설정한 키워드에 새로운 소식이 생기면 카카오 알림톡으로 알려드립니다.
            <br />
            {!userResponse?.data?.hasPhoneNumber && (
              <>
                등록된 전화번호가 없으면 카카오 알림톡 발송이 불가합니다.
                <br />
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '12px',
                    maxWidth: '460px',
                  }}
                >
                  <Cleave
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.rawValue)}
                    placeholder="010-1234-5678"
                    options={{
                      phone: true,
                      phoneRegionCode: 'KR',
                      delimiter: '-',
                    }}
                    className="ant-input"
                    style={{
                      flex: 1,
                    }}
                  />
                  <Button
                    disabled={phoneNumber === normalizedPhoneNumber}
                    onClick={async () => {
                      if (
                        !/^(01[016789]{1})[0-9]{3,4}[0-9]{4}$/.test(phoneNumber)
                      ) {
                        alert('핸드폰 번호 형식이 올바르지 않습니다.');
                        return;
                      }
                      await TeambinderApiService.modifyPhoneNumber({
                        requestBody: { phoneNumber },
                      });
                      refetchSetting();
                    }}
                  >
                    저장
                  </Button>
                </div>
              </>
            )}
            {!userResponse?.data?.notification?.kakao?.mobile &&
              userResponse?.data?.hasPhoneNumber && (
                <>
                  등록된 전화번호가 없으면 카카오 알림톡 발송이 불가합니다.{' '}
                  <br />
                  <Button size="small" type="link" style={{ padding: 0 }}>
                    <a
                      href={`https://kauth.kakao.com/oauth/authorize?client_id=7fa35d903c130394963af980d96edd38&redirect_uri=${encodeURI(
                        KAKAO_REDIRECT_URL_COMMAND,
                      )}&response_type=code`}
                    >
                      핸드폰 번호 등록하기
                    </a>
                  </Button>
                </>
              )}
          </div>
        </div>

        <div>
          <h4 style={{ display: 'inline-block', margin: 0 }}>
            알림톡 발송 빈도
          </h4>
          {['FREE', 'BASIC'].includes(currentPlan) && (
            <div style={{ marginTop: '8px', marginBottom: '10px' }}>
              구독 중인 요금제에 따라 알림톡 발송 빈도가 제한됩니다.
            </div>
          )}
          <div style={{ marginTop: '8px' }}>
            <div
              style={{
                width: '100%',
                maxWidth: '460px',
              }}
            >
              <Radio.Group
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
                onChange={async (e) => {
                  if (
                    !userResponse?.data?.notification?.kakao?.intervalOption
                      ?.availableOptions?.[e.target.value]
                  ) {
                    confirm({
                      title: (
                        <div>
                          구독 중인 요금제에서 이용할 수 없는 옵션입니다.
                          <br />
                          나에게 맞는 플랜을 찾아보세요!
                        </div>
                      ),
                      okText: '플랜 보기',
                      onOk() {
                        window.location.href = '/app/my?tab=2';
                      },
                      cancelText: '취소',
                    });
                    return;
                  }

                  await TeambinderApiService.modifyPersonalSettings({
                    settingName: 'notification.kakao.interval',
                    value: e.target.value,
                  });
                  refetchSetting();
                }}
                value={
                  userResponse?.data?.notification?.kakao?.intervalOption?.value
                }
              >
                <Radio value={'ONCE_A_WEEK'} style={{ marginRight: 0 }}>
                  일주일 1회
                </Radio>
                <Radio value={'ONCE_A_DAY'} style={{ marginRight: 0 }}>
                  하루 1회
                </Radio>
                <Radio value={'AS_NEEDED'} style={{ marginRight: 0 }}>
                  수시로
                </Radio>
              </Radio.Group>
            </div>
          </div>
        </div>
      </Card>

      {/* 이메일 알림 설정 카드 */}
      <Card style={cardStyle}>
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ display: 'inline-block', margin: 0 }}>
            이메일로 알림 받기
            <Switch
              checked={userResponse?.data?.notification?.email?.enabled}
              style={{ marginLeft: '10px' }}
              onClick={async (checked) => {
                await TeambinderApiService.modifyPersonalSettings({
                  settingName: 'notification.email.enabled',
                  value: String(checked),
                });
                refetchSetting();
              }}
            />
          </h4>
          <div style={{ marginTop: '8px' }}>
            설정한 키워드에 새로운 소식이 생기면 이메일로 알려드립니다.
            <br />
            <div
              style={{
                display: 'flex',
                gap: '8px',
                marginTop: '12px',
                maxWidth: '460px',
              }}
            >
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="이메일 주소"
                style={{ flex: 1 }}
              />
              <Button
                disabled={
                  email ===
                  (userResponse?.data?.notification?.email?.email || '')
                }
                onClick={async () => {
                  if (
                    !/^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,10})$/.test(
                      email,
                    )
                  ) {
                    alert('이메일 형식이 올바르지 않습니다.');
                    return;
                  }
                  await TeambinderApiService.modifyEmail({
                    requestBody: { email },
                  });
                  refetchSetting();
                }}
              >
                저장
              </Button>
            </div>
          </div>
        </div>

        <div>
          <h4 style={{ display: 'inline-block', margin: 0 }}>
            이메일 발송 빈도
          </h4>
          <div style={{ marginTop: '8px' }}>
            <div
              style={{
                width: '100%',
                maxWidth: '460px',
              }}
            >
              <Radio.Group
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
                onChange={async (e) => {
                  if (
                    !userResponse?.data?.notification?.email?.intervalOption
                      ?.availableOptions?.[e.target.value]
                  ) {
                    confirm({
                      title: (
                        <div>
                          구독 중인 요금제에서 이용할 수 없는 옵션입니다.
                          <br />
                          나에게 맞는 플랜을 찾아보세요!
                        </div>
                      ),
                      okText: '플랜 보기',
                      onOk() {
                        window.location.href = '/app/my?tab=2';
                      },
                      cancelText: '취소',
                    });
                    return;
                  }

                  await TeambinderApiService.modifyPersonalSettings({
                    settingName: 'notification.email.interval',
                    value: e.target.value,
                  });
                  refetchSetting();
                }}
                value={
                  userResponse?.data?.notification?.email?.intervalOption?.value
                }
              >
                <Radio value={'ONCE_A_WEEK'} style={{ marginRight: 0 }}>
                  일주일 1회
                </Radio>
                <Radio value={'ONCE_A_DAY'} style={{ marginRight: 0 }}>
                  하루 1회
                </Radio>
                <Radio value={'AS_NEEDED'} style={{ marginRight: 0 }}>
                  수시로
                </Radio>
              </Radio.Group>
            </div>
          </div>
        </div>
      </Card>

      {/* 서비스 설정 현황 카드 */}
      <Card style={cardStyle}>
        <h4
          style={{ display: 'inline-block', margin: 0, marginBottom: '16px' }}
        >
          서비스 설정 현황
        </h4>
        <SettingQuotaPane />
      </Card>

      {/* 기타 설정 카드 */}
      <Card style={cardStyle}>
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ display: 'inline-block', margin: 0 }}>
            배경화면 테마
            <Switch
              checked={currentTheme === 'dark'}
              style={{ marginLeft: '10px' }}
              onClick={handleClickSetTheme}
            />
          </h4>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: 0, marginBottom: '8px' }}>문의</h4>
          <a href="mailto:mysignal.bob@gmail.com">mysignal.bob@gmail.com</a>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <Button type="link" onClick={logout} style={{ padding: 0 }}>
            로그아웃
          </Button>
          <Button
            type="link"
            danger
            style={{ padding: 0 }}
            onClick={() => {
              confirm({
                title: '정말 탈퇴하시겠습니까?',
                content: '모든 데이터가 삭제되며 복구할 수 없습니다.',
                okText: '탈퇴',
                okButtonProps: { danger: true },
                cancelText: '취소',
                async onOk() {
                  try {
                    await TeambinderApiService.withdrawMembership1();
                    info({
                      title:
                        '바인더를 이용해주셔서 감사합니다. 탈퇴가 완료되었습니다.',
                      onOk() {
                        window.location.href = 'https://www.teambinder.ai';
                      },
                    });
                  } catch {
                    info({
                      title: '탈퇴에 실패했습니다.',
                      content: '잠시 후 다시 시도해주세요.',
                    });
                  } finally {
                    localStorage.clear();
                  }
                },
              });
            }}
          >
            탈퇴
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default TabInfo;
